// Aviso de privacidad / derecho a la información (LOPDP Art. 12).
// - AvisoPrivacidad: pantalla de administración (Sistemas → Gestión de Autenticación).
// - AvisoPrivacidadModal: visor de solo lectura, reutilizable (portal, login, etc.).
import { useEffect, useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { FileLock2, Loader2 } from 'lucide-react';
import { toast } from 'sonner';
import { apiClient, getErrorMessage } from '@/lib/api/client';
import { useAuth } from '@/lib/hooks';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/Card';
import { Button } from '@/components/ui/Button';
import { Campo, Modal, fieldCls } from './shared';

export interface Aviso {
  id?: string; version?: string; responsable?: string; responsable_contacto?: string;
  dpo_contacto?: string; finalidades?: string; base_legal?: string; conservacion?: string;
  derechos?: string; transferencias?: string; texto?: string; updated_at?: string;
}

const rpc = async <T,>(fn: string, body: Record<string, unknown> = {}): Promise<T> =>
  (await apiClient.post<T>(`/rpc/${fn}`, body)).data;

const CAMPOS: Array<{ k: keyof Aviso; label: string; help?: string }> = [
  { k: 'responsable', label: 'Responsable del tratamiento', help: 'Nombre o razón social' },
  { k: 'responsable_contacto', label: 'Contacto del responsable', help: 'Dirección, correo, teléfono' },
  { k: 'dpo_contacto', label: 'Delegado de Protección de Datos (DPO)', help: 'Texto de respaldo. Si hay una designación activa en “Delegado de Protección”, el aviso muestra ese contacto en su lugar.' },
  { k: 'finalidades', label: 'Finalidades del tratamiento' },
  { k: 'base_legal', label: 'Base legal' },
  { k: 'conservacion', label: 'Tiempo de conservación' },
  { k: 'derechos', label: 'Derechos del titular y cómo ejercerlos' },
  { k: 'transferencias', label: 'Transferencias / comunicaciones a terceros' },
  { k: 'texto', label: 'Introducción / cláusulas adicionales' },
];

export function AvisoPrivacidad() {
  const qc = useQueryClient();
  const { data, isFetching } = useQuery<Aviso | null>({ queryKey: ['aviso-privacidad'], queryFn: () => rpc<Aviso | null>('aviso_privacidad') });
  const [form, setForm] = useState<Aviso>({});
  useEffect(() => { if (data) setForm(data); }, [data]);
  const set = (k: keyof Aviso, v: string) => setForm((f) => ({ ...f, [k]: v }));

  const guardar = useMutation({
    mutationFn: () => rpc('aviso_privacidad_guardar', { _data: form }),
    onSuccess: () => { toast.success('Aviso de privacidad guardado'); void qc.invalidateQueries({ queryKey: ['aviso-privacidad'] }); },
    onError: (e) => toast.error(getErrorMessage(e)),
  });

  return (
    <div className="p-6 space-y-4 max-w-3xl">
      <div>
        <h1 className="text-xl font-semibold flex items-center gap-2"><FileLock2 className="w-5 h-5 text-primary" /> Aviso de privacidad</h1>
        <p className="text-sm text-muted-foreground">Información al titular sobre el tratamiento de sus datos (Ley Orgánica de Protección de Datos Personales, Art. 12). Se muestra en el portal del cliente/paciente.</p>
      </div>

      <Card>
        <CardContent className="pt-4 space-y-3">
          {isFetching && <div className="py-4 text-center"><Loader2 className="w-5 h-5 animate-spin mx-auto text-primary" /></div>}
          <div className="grid grid-cols-2 gap-3">
            <Campo label="Versión"><input className={fieldCls} value={form.version ?? ''} onChange={(e) => set('version', e.target.value)} /></Campo>
          </div>
          {CAMPOS.map(({ k, label, help }) => (
            <Campo key={k} label={label}>
              <textarea className={`${fieldCls} ${k === 'texto' || k === 'derechos' || k === 'finalidades' ? 'h-24' : 'h-16'}`} value={(form[k] as string) ?? ''} onChange={(e) => set(k, e.target.value)} />
              {help && <span className="text-xs text-muted-foreground">{help}</span>}
            </Campo>
          ))}
          <div className="flex justify-end">
            <Button isLoading={guardar.isPending} disabled={!form.responsable?.trim()} onClick={() => guardar.mutate()}>Guardar</Button>
          </div>
        </CardContent>
      </Card>
    </div>
  );
}

// ---- Visor de solo lectura (modal) ----
const SECCIONES: Array<{ k: keyof Aviso; t: string }> = [
  { k: 'finalidades', t: 'Finalidades del tratamiento' },
  { k: 'base_legal', t: 'Base legal' },
  { k: 'conservacion', t: 'Tiempo de conservación' },
  { k: 'transferencias', t: 'Comunicaciones a terceros' },
  { k: 'derechos', t: 'Sus derechos' },
];

export function AvisoPrivacidadContenido({ aviso }: { aviso: Aviso }) {
  return (
    <div className="space-y-3 text-sm">
      {aviso.texto && <p className="text-muted-foreground">{aviso.texto}</p>}
      <div className="rounded-md border p-3 space-y-0.5">
        {aviso.responsable && <div><b>Responsable:</b> {aviso.responsable}</div>}
        {aviso.responsable_contacto && <div className="whitespace-pre-line"><b>Contacto:</b> {aviso.responsable_contacto}</div>}
        {aviso.dpo_contacto && <div><b>Delegado de Protección de Datos:</b> {aviso.dpo_contacto}</div>}
      </div>
      {SECCIONES.filter((s) => aviso[s.k]).map((s) => (
        <div key={s.k}><h4 className="font-semibold">{s.t}</h4><p className="text-muted-foreground whitespace-pre-line">{aviso[s.k] as string}</p></div>
      ))}
      {aviso.version && <p className="text-xs text-muted-foreground pt-1">Versión {aviso.version}</p>}
    </div>
  );
}

export function AvisoPrivacidadModal({ onClose }: { onClose: () => void }) {
  const { data, isFetching } = useQuery<Aviso | null>({ queryKey: ['aviso-privacidad'], queryFn: () => rpc<Aviso | null>('aviso_privacidad') });
  return (
    <Modal title="Aviso de privacidad" onClose={onClose} wide>
      {isFetching ? <div className="py-6 text-center"><Loader2 className="w-5 h-5 animate-spin mx-auto text-primary" /></div>
        : data ? <AvisoPrivacidadContenido aviso={data} />
        : <p className="text-sm text-muted-foreground">Aún no se ha publicado un aviso de privacidad.</p>}
    </Modal>
  );
}

// Gate al ingresar: si el usuario no ha aceptado el aviso vigente, lo bloquea
// con un modal hasta que lo acepte (o cierre sesión). Re-aparece si cambia la versión.
// Puerta integrada (sin ventana modal): mientras haya un aviso pendiente de
// aceptar, el aviso ocupa el lugar del contenido de la app como una página.
export function AvisoPrivacidadGate({ children }: { children?: React.ReactNode }) {
  const qc = useQueryClient();
  const { logout } = useAuth();
  const [acepto, setAcepto] = useState(false);
  const { data } = useQuery<Aviso | null>({
    queryKey: ['avisoPendiente'],
    queryFn: () => rpc<Aviso | null>('aviso_pendiente'),
    staleTime: 5 * 60 * 1000,
  });
  const aceptar = useMutation({
    mutationFn: () => rpc('aviso_aceptar', { _version: data?.version }),
    onSuccess: () => { void qc.invalidateQueries({ queryKey: ['avisoPendiente'] }); },
    onError: (e) => toast.error(getErrorMessage(e)),
  });

  if (!data) return <>{children}</>;
  return (
    <div className="flex h-full items-start justify-center overflow-y-auto p-4">
      <Card className="w-full max-w-2xl my-8">
        <CardHeader className="pb-2">
          <CardTitle className="text-base flex items-center gap-2"><FileLock2 className="w-4 h-4 text-primary" /> Aviso de privacidad</CardTitle>
        </CardHeader>
        <CardContent className="space-y-3">
          <p className="text-sm text-muted-foreground">Para continuar, lee y acepta cómo tratamos tus datos personales (Ley Orgánica de Protección de Datos Personales).</p>
          <div className="max-h-[55vh] overflow-y-auto rounded-md border bg-muted/30 p-3">
            <AvisoPrivacidadContenido aviso={data} />
          </div>
          <label className="flex items-start gap-2 text-sm">
            <input type="checkbox" className="mt-1" checked={acepto} onChange={(e) => setAcepto(e.target.checked)} />
            <span>He leído y acepto el aviso de privacidad y el tratamiento de mis datos personales conforme a lo descrito.</span>
          </label>
          <div className="flex justify-end gap-2 border-t pt-3">
            <Button variant="outline" onClick={() => logout()}>Cerrar sesión</Button>
            <Button disabled={!acepto} isLoading={aceptar.isPending} onClick={() => aceptar.mutate()}>Aceptar y continuar</Button>
          </div>
        </CardContent>
      </Card>
    </div>
  );
}
